import { useState, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Upload, Loader2, FileText, Check, Search } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "accepted", "partial_paid", "paid", "voided"];

const UploadQuoteDialog = ({ open, onOpenChange, customers = [], onCreated }) => {
  const [custSearch, setCustSearch] = useState("");
  const [customer, setCustomer] = useState(null);
  const [total, setTotal] = useState("");
  const [status, setStatus] = useState("sent");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null); // { name, dataUrl }
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setCustSearch(""); setCustomer(null); setTotal(""); setStatus("sent"); setNotes(""); setFile(null);
  };

  const matches = useMemo(() => {
    const t = custSearch.trim().toLowerCase();
    if (!t || customer) return [];
    return customers
      .filter((c) => [c.name, c.email, c.company, c.address].some((v) => (v || "").toLowerCase().includes(t)))
      .slice(0, 8);
  }, [custSearch, customers, customer]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Please choose a PDF file"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("PDF must be under 15 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, dataUrl: reader.result });
    reader.onerror = () => toast.error("Could not read that file");
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!customer) { toast.error("Pick a customer"); return; }
    if (!file) { toast.error("Attach the quote PDF"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/quotes/external`, {
        customer_id: customer.id,
        customer_name: customer.name,
        customer_email: customer.email || null,
        customer_phone: customer.phone || null,
        property_address: customer.address || null,
        subtotal: total ? Number(total) : 0,
        status,
        notes: notes.trim() || null,
        quote_pdf: file.dataUrl,
        quote_pdf_filename: file.name,
      });
      toast.success("Quote uploaded");
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Quote</DialogTitle>
          <DialogDescription>Attach a PDF made outside the app, assign a customer, and set a status. It gets the next quote number automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Customer picker */}
          {customer ? (
            <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="upload-selected-customer">
              <div>
                <div className="font-medium text-emerald-900">{customer.name}</div>
                <div className="text-xs text-emerald-700">{customer.email || customer.address || ""}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setCustomer(null)}>Change</Button>
            </div>
          ) : (
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" placeholder="Search customer by name, email, address…" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} data-testid="upload-customer-search" />
              {matches.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {matches.map((c) => (
                    <button key={c.id} type="button" className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm" onClick={() => { setCustomer(c); setCustSearch(""); }} data-testid={`upload-customer-option-${c.id}`}>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.email || c.address || ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PDF picker */}
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onPick} data-testid="upload-quote-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-3 hover:bg-gray-50 text-left" data-testid="upload-quote-pick-file">
            {file ? <Check className="w-5 h-5 text-emerald-600" /> : <FileText className="w-5 h-5 text-gray-400" />}
            <span className="text-sm text-gray-700 truncate">{file ? file.name : "Choose PDF file…"}</span>
          </button>

          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Total ($)" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="upload-quote-total" />
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="upload-quote-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
          </div>
          <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="upload-quote-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#00341d] hover:bg-[#002816]" data-testid="upload-quote-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload Quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadQuoteDialog;
